import type { DynamicClient } from '@dynamic-labs-sdk/client';
import { onEvent, onceEvent } from '@dynamic-labs-sdk/client';
import {
  WalletProviderPriority,
  getCore,
  getDefaultClient,
  getWalletProviderRegistry,
  hasExtension,
  registerExtension,
} from '@dynamic-labs-sdk/client/core';

import { registerSolanaNetworkProviderBuilder } from '../../registerSolanaNetworkProviderBuilder';
import { completePhantomRedirect } from '../completePhantomRedirect';
import { createPhantomRedirectWalletProvider } from '../createPhantomRedirectWalletProvider';
import { detectPhantomRedirect } from '../detectPhantomRedirect';

export const PHANTOM_REDIRECT_SOLANA_EXTENSION_KEY = 'phantomRedirectSolana';

export type AddPhantomRedirectSolanaExtensionParams = {
  /**
   * When `true`, the extension will **not** automatically detect or complete
   * Phantom redirects on initialization. Use this when you want to call
   * `completePhantomRedirect` yourself at a custom point in your app's
   * lifecycle.
   *
   * When `disableAutoRedirectCompletion` is `true` the `onCloseTab` callback
   * is not auto-registered by the extension, so you are responsible for
   * listening to `phantomRedirectCloseTab` manually if needed.
   */
  disableAutoRedirectCompletion?: boolean;

  /**
   * Called when the SDK determines that the current tab is a **clone tab** —
   * a new browser tab that Phantom opened to deliver the redirect callback on
   * mobile browsers. Once this fires, the clone tab has served its purpose and
   * should be closed so the user is returned to the originating tab.
   *
   * ### Typical usage
   *
   * ```ts
   * addPhantomRedirectSolanaExtension({
   *   url: new URL(window.location.href),
   *   onCloseTab: () => window.close(),
   * });
   * ```
   *
   * ### Opting out
   *
   * If you deliberately do not want automatic tab closing (e.g. you handle
   * navigation yourself), pass an explicit no-op to make the intent clear:
   *
   * ```ts
   * onCloseTab: () => {}
   * ```
   *
   * ### When is this NOT called?
   *
   * - **Mobile native** (React Native, Capacitor, etc.): the deep link is
   *   handled in the same JavaScript context as the originating page, so no
   *   clone tab is created and this callback is never invoked.
   * - **`disableAutoRedirectCompletion: true`**: the extension does not
   *   auto-register this callback. Subscribe to `phantomRedirectCloseTab`
   *   yourself in that case.
   *
   * ### Advanced: subscribing directly to the event
   *
   * For more control you can listen to the underlying `phantomRedirectCloseTab`
   * event directly. This is useful when you need to perform async work before
   * closing:
   *
   * ```ts
   * onEvent(
   *   { event: 'phantomRedirectCloseTab', listener: () => {
   *     await flushAnalytics();
   *     window.close();
   *   }},
   *   dynamicClient
   * );
   * ```
   */
  onCloseTab: () => void;

  /**
   * The current page URL. Used both to detect an incoming Phantom redirect
   * callback and to derive the `baseRedirectUrl` for deep links.
   */
  url: URL;
};

/**
 * Adds the Phantom redirect Solana extension to the Dynamic client.
 *
 * ### What this extension does
 *
 * This extension enables Phantom wallet integration via deep link redirects,
 * designed for mobile environments where a browser extension is not available.
 *
 * On initialization it:
 * 1. Registers the internal Dynamic extension logic handlers.
 * 2. Detects whether the current URL is a Phantom redirect callback and, if
 *    so, completes the pending operation (decrypt response, emit events,
 *    broadcast to originating tab).
 * 3. Sets up the `onCloseTab` listener so that the clone tab is closed after completion.
 *
 * ### Mobile browser redirect flow
 *
 * ```
 * [Tab 1 — your app]              [Phantom app / PWA]      [Tab 2 — clone]
 *   connect()
 *   openDeeplink(phantomUrl) ─────────────►
 *                                  user approves
 *                                  redirect to app ───────────────►
 *                                                           completePhantomRedirect()
 *                                                           broadcastPhantomEvent()
 *                                                           emits phantomRedirectCloseTab
 *                                                           onCloseTab() → window.close()
 *   BroadcastChannel receives ◄──────────────────────────────────────────────
 *   deferredPromise.resolve()
 * ```
 *
 * ### Mobile native flow (React Native / Capacitor)
 *
 * The deep link is delivered to the same JavaScript context, so `Tab 2` never
 * exists. `completePhantomRedirect` detects this via a `sessionStorage`
 * marker written during request initiation and does **not** emit
 * `phantomRedirectCloseTab`.
 *
 * @param params.url - The current page URL
 * @param params.onCloseTab - Callback invoked when the clone tab should close
 * @param params.disableAutoRedirectCompletion - Skip auto detection/completion
 * @param [client] - The Dynamic client. Only required for multiple clients.
 */
export const addPhantomRedirectSolanaExtension = async (
  {
    disableAutoRedirectCompletion,
    onCloseTab,
    url,
  }: AddPhantomRedirectSolanaExtensionParams,
  client: DynamicClient = getDefaultClient()
): Promise<void> => {
  if (
    hasExtension(
      { extensionKey: PHANTOM_REDIRECT_SOLANA_EXTENSION_KEY },
      client
    )
  ) {
    getCore(client).logger.debug(
      '[PHANTOM] addPhantomRedirectSolanaExtension: already registered, skipping'
    );
    return;
  }

  registerExtension(
    { extensionKey: PHANTOM_REDIRECT_SOLANA_EXTENSION_KEY },
    client
  );
  registerSolanaNetworkProviderBuilder(client);

  const core = getCore(client);

  if (!disableAutoRedirectCompletion) {
    const isRedirect = await detectPhantomRedirect({ url }, client);

    if (isRedirect) {
      // Register the close-tab handler before completion starts so it is
      // guaranteed to be in place when completePhantomRedirect emits the event.
      onceEvent(
        { event: 'phantomRedirectCloseTab', listener: onCloseTab },
        client
      );

      core.initTrack.track({
        name: 'phantomRedirectCompletion',
        promise: completePhantomRedirect({ url }, client).catch((error) => {
          core.logger.debug(
            '[PHANTOM] addPhantomRedirectSolanaExtension: completion failed',
            { error }
          );
          core.logger.error('Phantom redirect completion failed:', error);
        }),
      });
    }
  }

  const baseRedirectUrl = `${url.protocol}//${url.host}${url.pathname}`;

  const walletProvider = createPhantomRedirectWalletProvider({
    baseRedirectUrl,
    dynamicClient: client,
  });

  const walletProviderRegistry = getWalletProviderRegistry(client);

  const isConflictingProvider = (provider: {
    chain: string;
    groupKey: string;
    key: string;
  }) =>
    provider.chain === walletProvider.chain &&
    provider.groupKey === walletProvider.groupKey &&
    provider.key !== walletProvider.key;

  const existingConflict = walletProviderRegistry
    .listProviders()
    .find(isConflictingProvider);

  if (existingConflict) {
    core.logger.debug(
      '[PHANTOM] addPhantomRedirectSolanaExtension: skipping registration, conflicting provider exists',
      { conflictingKey: existingConflict.key }
    );
    return;
  }

  walletProviderRegistry.register({
    priority: WalletProviderPriority.WINDOW_INJECT,
    walletProvider,
  });

  const removeListener = onEvent(
    {
      event: 'walletProviderRegistered',
      listener: ({ walletProvider: newProvider }) => {
        if (isConflictingProvider(newProvider)) {
          core.logger.debug(
            '[PHANTOM] addPhantomRedirectSolanaExtension: unregistering, conflicting provider registered',
            { conflictingKey: newProvider.key }
          );
          walletProviderRegistry.unregister(walletProvider.key);
          removeListener();
        }
      },
    },
    client
  );
};
